import { useEffect, useState } from 'react';

import NEW_DASHBOARD_URL from '@/config/new-dashboard.config';
import NoticeLayout, {
  NoticeArrowIcon,
  NoticeEyebrow,
  NoticeFootnote,
  NoticeHeading,
  NoticeIconBadge,
  NoticeStatus,
  NoticeText,
} from '@/layouts/notice.layout';

const REDIRECT_SECONDS = 1;

const MovedIcon = () => (
  <svg
    viewBox="0 0 96 80"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-[5.25rem] h-[4.375rem]"
    aria-hidden="true"
  >
    {/* old window, its lower-left corner turning into the arrow */}
    <path d="M4 34V10a6 6 0 0 1 6-6h40a6 6 0 0 1 6 6v20" />
    <path d="M4 16h52" />
    <path d="M4 34v4a8 8 0 0 0 8 8h22" />
    <path d="M28 40l6 6-6 6" />
    {/* new window */}
    <rect x="40" y="30" width="52" height="44" rx="6" />
    <path d="M40 42h52" />
    <g fill="currentColor" stroke="none">
      <circle cx="13" cy="10" r="1.8" />
      <circle cx="20" cy="10" r="1.8" />
      <circle cx="27" cy="10" r="1.8" />
      <circle cx="49" cy="36" r="1.8" />
      <circle cx="56" cy="36" r="1.8" />
      <circle cx="63" cy="36" r="1.8" />
    </g>
  </svg>
);

const GlobeIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    className="w-6 h-6 shrink-0"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18" />
    <path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18" />
  </svg>
);

const ExternalIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-5 h-5 shrink-0"
    aria-hidden="true"
  >
    <path d="M14 4h6v6" />
    <path d="M20 4l-9 9" />
    <path d="M18 14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4" />
  </svg>
);

/**
 * Shown on every route of this legacy app (except the extension login
 * handoff). Tells the user the dashboard has moved and sends them to the new
 * one after a short countdown.
 */
const DeprecatedPage = () => {
  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_SECONDS);

  useEffect(() => {
    document.title = 'NexLev has moved';
  }, []);

  useEffect(() => {
    if (secondsLeft <= 0) {
      // `replace` keeps the legacy URL out of history, so Back doesn't bounce
      // the user straight into another redirect.
      window.location.replace(NEW_DASHBOARD_URL);
      return undefined;
    }

    const timer = window.setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [secondsLeft]);

  const redirectMessage =
    secondsLeft > 0
      ? `Redirecting you in ${secondsLeft} second${
          secondsLeft === 1 ? '' : 's'
        }…`
      : 'Redirecting you now…';

  return (
    <NoticeLayout>
      <NoticeIconBadge>
        <MovedIcon />
      </NoticeIconBadge>

      <NoticeEyebrow>A new home for NexLev</NoticeEyebrow>

      <NoticeHeading>
        Your dashboard
        <br />
        has a new home.
      </NoticeHeading>

      <NoticeText>
        We’ve moved to a new dashboard. Continue to the new address below.
      </NoticeText>

      <a
        href={NEW_DASHBOARD_URL}
        className="mt-6 w-full flex items-center gap-3 rounded-xl bg-[#EDF2FA] hover:bg-[#E3EAF6] px-4 py-3.5 text-left transition-colors"
      >
        <span className="text-[#4A5F86]">
          <GlobeIcon />
        </span>
        <span className="flex-1 text-lg font-semibold text-[#0B1220]">
          dashboard.nexlev.io
        </span>
        <span className="text-[#4A5F86]">
          <ExternalIcon />
        </span>
      </a>

      <a
        href={NEW_DASHBOARD_URL}
        className="mt-5 w-full flex items-center justify-center gap-2.5 rounded-xl bg-[#0A3DDC] hover:bg-[#0832B8] py-3.5 text-lg font-semibold text-white transition-colors"
      >
        Go to new dashboard
        <NoticeArrowIcon />
      </a>

      <NoticeStatus>{redirectMessage}</NoticeStatus>

      <NoticeFootnote>This dashboard is no longer available.</NoticeFootnote>
    </NoticeLayout>
  );
};

export default DeprecatedPage;
